import { LinkButton, PageTitle } from '@sleepstation/components'
import { VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import { LAYOUT_ALIGN, LAYOUT_SPACING } from '@sleepstation/const'
import { marginBottom, marginTop } from '@sleepstation/style-api-helpers'
import { replaceTokens } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { pipe, prop, filter, map, path, gt, includes } from 'ramda'
import { CopyParagraph } from '@sleepstation/components-copy'
import infoCardThemeBlue from '../../../../themes/infoCardThemeBlue'
import BreakoutModuleNarrower from '../../../shared/breakoutModules/BreakoutModuleNarrower'
import InfoCard from '../../../shared/InfoCards/InfoCard'
import { getRangeDbas } from '../../../../utils/dbas'
import Beliefs from './Beliefs'
import { RANGE_DBAS } from '../../../../const/dbas'
import ImageCtaCard from '../../HubEpworthQuizResultsModule/HubEpworthQuizResultsPage/ImageCtaCard'
import LinkCard from '../../HubModule/HubPage/HubSectionsList/cards/LinkCard'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const BELIEFS_RANGES = [`Low flagged`, `High`]

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const selectHighScoreQuestions = pipe(
  prop(`nodes`),
  filter((node) => gt(Number(node.score), 5)),
  map(path([`step`, `contents`, `nodes`, 0, `value`]))
)

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const CardWrapper = styled.div`
  ${marginTop(1)}
  ${marginBottom(0.25)}
`

const Footer = styled.div.attrs({ 'data-cy': `retake-dbas-link` })`
  ${marginTop([1, 2])}
  > * {
    width: 100%;
    display: block;
    max-width: 346px;
    margin: 0 auto;
  }
`
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const HubDbasQuizResultsPage = ({ content, results }) => {
  const { score, result } = results
  const highScoreQuestions = selectHighScoreQuestions(results.stepResponses)
  const title = replaceTokens(content.titleTemplate, {
    score,
  })
  const range = getRangeDbas(result)
  const explanationAst = content.explanation[range]
  const beliefsExplanationAst = content.beliefs.explanation[range]
  const beliefsTitleAst = content.beliefs.title[range]
  const showBeliefs = BELIEFS_RANGES.includes(result)
  const scoreTitle = replaceTokens(content.scoreTemplate, results)
  const shouldShowSleepCoachesCard = includes(range, [
    RANGE_DBAS.LOW,
    RANGE_DBAS.LOW_FLAGGED,
  ])
  const shouldShowResourcesCard = includes(range, [RANGE_DBAS.HIGH])
  return (
    <BreakoutModuleNarrower
      hasBottomBorder={false}
      hasGradient={false}
      paddingV={[[0, 0.25]]}
      marginV={[
        [1.5, 2],
        [1.5, 3],
        [2.5, 4],
      ]}
    >
      <VLayout spacing={[LAYOUT_SPACING.LARGE]} align={LAYOUT_ALIGN.STRETCH}>
        <PageTitle align="center">{title}</PageTitle>
        <ThemeProvider theme={infoCardThemeBlue}>
          <InfoCard title={scoreTitle} bodyAst={explanationAst} />
        </ThemeProvider>
        {showBeliefs && (
          <Beliefs title={beliefsTitleAst} beliefs={highScoreQuestions} />
        )}
        <CopyRenderer htmlAst={beliefsExplanationAst} />
        {shouldShowSleepCoachesCard && (
          <CardWrapper>
            <ImageCtaCard {...content.sleepCoachesCard} />
          </CardWrapper>
        )}
        {shouldShowResourcesCard && (
          <CardWrapper>
            <LinkCard {...content.resourcesCard} />
          </CardWrapper>
        )}
        <CopyParagraph>{content.buttonText}</CopyParagraph>
        <Footer>
          <LinkButton
            href={content.retakeQuestionnaireButton.url}
            kind="secondary"
          >
            {content.retakeQuestionnaireButton.label}
          </LinkButton>
        </Footer>
      </VLayout>
    </BreakoutModuleNarrower>
  )
}

HubDbasQuizResultsPage.propTypes = {
  content: PropTypes.object.isRequired,
  results: PropTypes.object.isRequired,
}

export default HubDbasQuizResultsPage
